// SYNC: duplicated verbatim across cres-t1/t13/t5/t6 (separate frontend apps, no shared package) —
// keep the copies in sync. The assistant BACKEND is centralized in cres-aws-t2; only this UI is copied.
// Parkour-AI Assistant — a chat launcher for the NavBar. The assistant backend is centralized in the
// Developer (t2) app and reached cross-app (see data/assistant.ts); this app only renders the chat.
// Phase 1 is a read-only analyst (platform exceptions/errors over the shared DB); its scope will grow.
// Per-tool "activity" steps come back on the HTTP response and render under the reply. (No MQTT
// live-ticks here: the t2 backend pushes to its own broker/topic, not this app's.)
import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    IconButton, Popover, Box, Typography, Tooltip, TextField, CircularProgress,
} from "@mui/material";
import {AutoAwesomeOutlined, SendRounded} from "@mui/icons-material";
import {sendAssistantMessage} from "../../data/assistant";
import {useSessionContext} from "../../util/SessionContext";

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const TEXT = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E4E6E8";
const BLUE = "#1498D8";

type ChatMessage = {
    role: "user" | "assistant" | "error";
    text: string;
    model?: string;
    steps?: string[];
};

const GREETING: ChatMessage = {
    role: "assistant",
    text: "Hi — I'm the Parkour-AI Assistant. I can investigate platform exceptions and errors: ask me to find recent exceptions or to explain and diagnose a specific failure.",
};

export default function AssistantChat() {
    // THE SESSION, because the assistant now goes through this app's own route rather than t2's.
    const sessionContext = useSessionContext();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
    const [rawMessages, setRawMessages] = useState<any[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    const listRef = useRef<HTMLDivElement | null>(null);
    const scrollToBottom = useCallback(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, []);
    useEffect(() => {
        scrollToBottom();
    }, [messages, loading, scrollToBottom]);

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput("");
        setMessages((prev) => [...prev, {role: "user", text}]);
        setLoading(true);
        try {
            const turn = await sendAssistantMessage(sessionContext, text, rawMessages);
            setRawMessages(turn.messages);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    text: turn.reply || "(no response)",
                    model: turn.model || undefined,
                    steps: turn.activity.map((a) => a.text).filter(Boolean),
                },
            ]);
        } catch {
            setMessages((prev) => [
                ...prev,
                {role: "error", text: "Sorry — something went wrong reaching the assistant. Please try again."},
            ]);
        } finally {
            setLoading(false);
        }
    }, [input, loading, rawMessages]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <>
            <Tooltip title="Parkour-AI Assistant">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="assistant">
                    <AutoAwesomeOutlined sx={{color: BLUE}} />
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {width: 420, maxWidth: "92vw", height: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", fontFamily: FONT}}}}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`}}>
                    <AutoAwesomeOutlined sx={{color: BLUE, fontSize: 20}} />
                    <Typography sx={{fontSize: 15, fontWeight: 700, color: TEXT, fontFamily: FONT}}>Parkour-AI Assistant</Typography>
                </Box>

                <Box ref={listRef} sx={{flex: 1, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1.25}}>
                    {messages.map((m, i) => (
                        <Box
                            key={i}
                            sx={{
                                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                                maxWidth: "88%",
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                fontFamily: FONT,
                                fontSize: 13.5,
                                lineHeight: 1.5,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: m.role === "error" ? "#B3261E" : TEXT,
                                backgroundColor: m.role === "user" ? "#E9F4FB" : m.role === "error" ? "#FCECEA" : "#F5F6F7",
                                border: m.role === "user" ? `1px solid ${BORDER}` : "none",
                            }}
                        >
                            {m.role === "assistant" && m.steps && m.steps.length > 0 && (
                                <Box sx={{mb: 0.75}}>
                                    {m.steps.map((s, j) => (
                                        <Typography key={j} sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT}}>
                                            ✓ {s}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {m.text}
                            {m.role === "assistant" && m.model && (
                                <Typography component="div" sx={{fontSize: 10, color: MUTED, fontFamily: FONT, mt: 0.5}}>
                                    {m.model}
                                </Typography>
                            )}
                        </Box>
                    ))}

                    {loading && (
                        <Box sx={{alignSelf: "flex-start", maxWidth: "88%", px: 1.5, py: 1}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <CircularProgress size={13} sx={{color: BLUE}} />
                                <Typography sx={{fontSize: 12.5, color: MUTED, fontFamily: FONT}}>Thinking…</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={{borderTop: `1px solid ${BORDER}`, px: 1.5, py: 1.25}}>
                    <Box sx={{display: "flex", alignItems: "flex-end", gap: 1}}>
                        <TextField
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Ask about a platform exception or error…"
                            multiline
                            maxRows={4}
                            fullWidth
                            size="small"
                            sx={{"& .MuiInputBase-input": {fontFamily: FONT, fontSize: 13.5}}}
                        />
                        <IconButton onClick={send} disabled={!input.trim() || loading} aria-label="send" sx={{color: BLUE}}>
                            <SendRounded />
                        </IconButton>
                    </Box>
                    <Typography sx={{fontSize: 10.5, color: MUTED, fontFamily: FONT, mt: 0.5, textAlign: "center"}}>
                        Read-only analysis. Nothing is created or changed.
                    </Typography>
                </Box>
            </Popover>
        </>
    );
}
